import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { ShoppingCart, LogOut, Settings, BarChart3, FileText, KeyRound, Package } from 'lucide-react';
import CrmNavLinks from '@/components/CrmNavLinks';

const MainLayout = ({ children, fullscreen = false, crmAdminOnly = false, wide = false }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div
      className={
        fullscreen
          ? 'flex flex-col h-screen overflow-hidden bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50'
          : 'min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50'
      }
    >
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-gray-200 sticky top-0 z-50 shadow-sm">
        <div className={wide ? "max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8" : "max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"}>
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center space-x-3 group">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center shadow-lg group-hover:scale-105 transition-transform">
                <span className="text-white font-bold text-xl">A</span>
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900" style={{ fontFamily: 'Space Grotesk' }}>АвтоВАЗ.KZ</h1>
                <p className="text-xs text-gray-500">Запчасти для авто</p>
              </div>
            </Link>

            {/* Navigation */}
            <div className="flex items-center space-x-4">
              {user && (
                <>
                  <Link to="/cart" data-testid="cart-button">
                    <Button variant="ghost" size="sm" className="relative">
                      <ShoppingCart className="w-5 h-5" />
                    </Button>
                  </Link>

                  <CrmNavLinks user={user} adminOnly={crmAdminOnly} />

                  <Link to="/orders" data-testid="orders-button">
                    <Button variant="ghost" size="sm">
                      <Package className="w-5 h-5 mr-2" />
                      Заказы
                    </Button>
                  </Link>

                  {user.is_admin && (
                    <>
                      <Link to="/reconciliation" data-testid="reconciliation-button">
                        <Button variant="ghost" size="sm">
                          <FileText className="w-5 h-5 mr-2" />
                          Акт сверки
                        </Button>
                      </Link>
                      <Link to="/stats" data-testid="stats-button">
                        <Button variant="ghost" size="sm">
                          <BarChart3 className="w-5 h-5 mr-2" />
                          Статистика
                        </Button>
                      </Link>
                      <Link to="/admin" data-testid="admin-button">
                        <Button variant="ghost" size="sm">
                          <Settings className="w-5 h-5 mr-2" />
                          Админ
                        </Button>
                      </Link>
                    </>
                  )}

                  <div className="flex items-center space-x-3 pl-4 border-l border-gray-200">
                    <div className="text-right">
                      <p className="text-sm font-medium text-gray-900">{user.name}</p>
                      <p className="text-xs text-gray-500">{user.email}</p>
                    </div>
                    <Link to="/change-password" title="Сменить пароль">
                      <Button variant="ghost" size="sm">
                        <KeyRound className="w-5 h-5" />
                      </Button>
                    </Link>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleLogout}
                      data-testid="logout-button"
                    >
                      <LogOut className="w-5 h-5" />
                    </Button>
                  </div>
                </>
              )}

              {!user && (
                <div className="flex items-center space-x-2">
                  <Link to="/login">
                    <Button variant="ghost" size="sm" data-testid="login-button">Вход</Button>
                  </Link>
                  <Link to="/register">
                    <Button size="sm" data-testid="register-button">Регистрация</Button>
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main
        className={
          fullscreen
            ? "flex flex-1 flex-col min-h-0 overflow-hidden p-0 max-w-none w-full"
            : wide
            ? "max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-6 w-full"
            : "max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8"
        }
      >
        {children}
      </main>

      {!fullscreen && (
        <footer className="bg-white/60 backdrop-blur-sm border-t border-gray-200 mt-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="text-center text-gray-600 text-sm">
              <p>&copy; 2025 АвтоВАЗ.KZ. Все права защищены.</p>
            </div>
          </div>
        </footer>
      )}
    </div>
  );
};

export default MainLayout;
